import React, { useState, useEffect, useRef, useMemo } from 'react';
import api, { getAdAccounts } from '../services/api';
import { 
  BarChart3, RefreshCw, Loader2, TrendingUp, Eye, MousePointer, 
  DollarSign, MessageCircle, ChevronDown, ChevronRight, Play, Pause, Edit3, X,
  Target, ShoppingCart, Filter, Search, Layers, Settings, Palette,
  Building2, CreditCard, CheckCircle, ArrowUpDown, ArrowUp, ArrowDown,
  Percent, Users, MoreVertical, Copy, Trash2, ExternalLink, Zap, Image
} from 'lucide-react';

const Campaigns = () => {
  // ============== STATE ==============
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [selectedCampaignFilter, setSelectedCampaignFilter] = useState('all');
  const [expandedAdsets, setExpandedAdsets] = useState({});
  const [adsets, setAdsets] = useState({});
  const [ads, setAds] = useState({});
  const [loadingAdsets, setLoadingAdsets] = useState({});
  const [loadingAds, setLoadingAds] = useState({});
  const [updating, setUpdating] = useState({});
  const [editBudget, setEditBudget] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);
  const [newBudget, setNewBudget] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Multi-select de contas
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [showAccountSelector, setShowAccountSelector] = useState(false);
  const [selectedBM, setSelectedBM] = useState('all');
  
  // Guias
  const [activeTab, setActiveTab] = useState('campaigns');
  
  // Seleção múltipla
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedAdsetItems, setSelectedAdsetItems] = useState([]);
  const [showBulkBudget, setShowBulkBudget] = useState(false);
  const [bulkBudgetMode, setBulkBudgetMode] = useState('set'); // set, increase, decrease
  const [bulkBudgetValue, setBulkBudgetValue] = useState('');
  const [bulkBudgetLoading, setBulkBudgetLoading] = useState(false);
  const [bulkDupLoading, setBulkDupLoading] = useState(false);
  
  // Ordenação
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  
  // Resize de colunas
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem('jtracker_campaign_col_widths');
    return saved ? JSON.parse(saved) : {};
  });
  
  const handleResizeStart = (e, colId) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = columnWidths[colId] || 120;
    
    const handleMouseMove = (moveE) => {
      const diff = moveE.clientX - startX;
      const newWidth = Math.max(60, startWidth + diff);
      setColumnWidths(prev => {
        const updated = { ...prev, [colId]: newWidth };
        localStorage.setItem('jtracker_campaign_col_widths', JSON.stringify(updated));
        return updated;
      });
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  // Colunas personalizáveis
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const DEFAULT_COLS = [
      { id: 'name', label: 'Nome', visible: true, fixed: true },
      { id: 'account', label: 'Conta', visible: true },
      { id: 'status', label: 'Status', visible: true },
      { id: 'budget', label: 'Orçamento', visible: true },
      { id: 'spend', label: 'Gasto', visible: true },
      { id: 'purchases', label: 'Compras', visible: true },
      { id: 'cpa', label: 'CPA', visible: true },
      { id: 'roas', label: 'ROAS', visible: true },
      { id: 'messages', label: 'Mensagens', visible: true },
      { id: 'cpl', label: 'CPL', visible: true },
      { id: 'clicks', label: 'Cliques', visible: false },
      { id: 'ctr', label: 'CTR', visible: false },
      { id: 'cpm', label: 'CPM', visible: false },
      { id: 'impressions', label: 'Impressões', visible: false },
      { id: 'reach', label: 'Alcance', visible: false },
      { id: 'purchaseValue', label: 'Valor Conversões', visible: true },
      { id: 'inlineLinkClicks', label: 'Cliques no Link', visible: true },
      { id: 'ctrLink', label: 'CTR Link', visible: true },
      // Revisao 15/07: metricas ausentes
      { id: 'frequency', label: 'Frequência', visible: false },
      { id: 'landingPageViews', label: 'Vis. Página de Destino', visible: false },
      { id: 'costPerLandingPageView', label: 'Custo/Vis. Página', visible: false },
      { id: 'postEngagement', label: 'Engajamento', visible: false },
      { id: 'postReactions', label: 'Curtidas Post', visible: false },
      { id: 'postComments', label: 'Comentários', visible: false },
      { id: 'postShares', label: 'Compartilhamentos', visible: false },
      { id: 'thruplays', label: 'ThruPlays', visible: false },
      { id: 'costPerThruplay', label: 'Custo/ThruPlay', visible: false },
      { id: 'videoPlays', label: 'Reprod. de Vídeo', visible: false },
      { id: 'video3s', label: 'Reprod. 3s+', visible: false },
      { id: 'videoP25', label: 'Reprod. 25%', visible: false },
      { id: 'videoP50', label: 'Reprod. 50%', visible: false },
      { id: 'videoP75', label: 'Reprod. 75%', visible: false },
      { id: 'videoP95', label: 'Reprod. 95%', visible: false },
      { id: 'videoP100', label: 'Reprod. 100%', visible: false },
      { id: 'videoAvgTime', label: 'Tempo Médio Vídeo', visible: false },
  ];
  const [columns, setColumns] = useState(() => {
    const saved = localStorage.getItem('jtracker_campaign_columns');
    if (!saved) return DEFAULT_COLS;
    const parsed = JSON.parse(saved);
    const savedIds = parsed.map(c => c.id);
    const newCols = DEFAULT_COLS.filter(c => !savedIds.includes(c.id));
    if (newCols.length > 0) {
      const merged = [...parsed];
      newCols.forEach(nc => {
        const origIdx = DEFAULT_COLS.findIndex(c => c.id === nc.id);
        merged.splice(Math.min(origIdx, merged.length), 0, nc);
      });
      return merged;
    }
    return parsed;
  });

  
  // Regras de destaque
  const [showHighlightRules, setShowHighlightRules] = useState(false);
  const [highlightRules, setHighlightRules] = useState(() => {
    const saved = localStorage.getItem('jtracker_highlight_rules');
    return saved ? JSON.parse(saved) : [
      { id: 'cpa', name: 'CPA', enabled: true, metric: 'cpa', green: { op: '<=', value: 150 }, yellow: { op: '<=', value: 200 }, red: { op: '>', value: 200 } },
      { id: 'roas', name: 'ROAS', enabled: true, metric: 'roas', green: { op: '>=', value: 2.5 }, yellow: { op: '>=', value: 1.5 }, red: { op: '<', value: 1.5 } },
    ];
  });

  const periods = [
    { value: 'today', label: 'Hoje' },
    { value: 'yesterday', label: 'Ontem' },
    { value: 'last_7d', label: 'Últimos 7 dias' },
    { value: 'last_14d', label: 'Últimos 14 dias' },
    { value: 'last_30d', label: 'Últimos 30 dias' },
    { value: 'this_month', label: 'Este mês' },
    { value: 'last_month', label: 'Mês passado' },
  ];

  // ============== FETCH DATA ==============
  const fetchAdAccounts = async () => {
    try {
      const response = await getAdAccounts();
      const accounts = response.data.accounts || [];
      setAdAccounts(accounts);
      const selected = accounts.filter(a => a.isSelected).map(a => a.adAccountId);
      setSelectedAccounts(selected.length > 0 ? selected : accounts.map(a => a.adAccountId));
    } catch (error) {
      console.error('Erro ao carregar contas:', error);
    }
  };

  const fetchData = async (forceSync = false) => {
    setLoading(true);
    try {
      if (forceSync) {
        await api.post('/campaigns/sync').catch(() => {});
      }
      const response = await api.get(`/campaigns?period=${period}`);
      setData(response.data);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdAccounts();
  }, []);

  useEffect(() => {
    fetchData();
  }, [period]);

  useEffect(() => {
    localStorage.setItem('jtracker_campaign_columns', JSON.stringify(columns));
  }, [columns]);

  useEffect(() => {
    localStorage.setItem('jtracker_highlight_rules', JSON.stringify(highlightRules));
  }, [highlightRules]);

  // Fechar menu de ação ao clicar fora
  useEffect(() => {
    if (!actionMenu) return;
    const handleClick = () => setActionMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [actionMenu]);

  // ============== HANDLERS ==============
  // Auto-carregar adsets/ads quando troca pra tab conjuntos/anúncios
  const loadAllAdsets = async () => {
    const campaignIds = filteredCampaigns.map(c => c.campaignId).filter(id => !adsets[id]);
    if (campaignIds.length === 0) return;
    for (const cId of campaignIds.slice(0, 50)) {
      try {
        const response = await api.get('/campaigns/' + cId + '/adsets?period=' + period);
        setAdsets(prev => ({ ...prev, [cId]: response.data.adsets || [] }));
      } catch (e) { console.error('Erro adsets:', e); }
    }
  };

  const loadAllAds = async () => {
    const allAdsetEntries = Object.entries(adsets);
    for (const [cId, sets] of allAdsetEntries) {
      for (const adset of sets) {
        if (!ads[adset.id]) {
          try {
            const response = await api.get('/campaigns/adset/' + adset.id + '/ads?period=' + period);
            setAds(prev => ({ ...prev, [adset.id]: response.data.ads || [] }));
          } catch (e) { console.error('Erro ads:', e); }
        }
      }
    }
  };

  const handleTabChange = async (tabId) => {
    setActiveTab(tabId);
    if (tabId === 'adsets') await loadAllAdsets();
    if (tabId === 'ads') { await loadAllAdsets(); await loadAllAds(); }
  };

  const toggleCampaign = async (campaignId) => {
    const isExpanded = expandedCampaigns[campaignId];
    setExpandedCampaigns(prev => ({ ...prev, [campaignId]: !isExpanded }));
    if (!isExpanded && !adsets[campaignId]) {
      setLoadingAdsets(prev => ({ ...prev, [campaignId]: true }));
      try {
        const response = await api.get(`/campaigns/${campaignId}/adsets?period=${period}`);
        setAdsets(prev => ({ ...prev, [campaignId]: response.data.adsets || [] }));
      } catch (error) {
        console.error('Erro ao carregar adsets:', error);
      } finally {
        setLoadingAdsets(prev => ({ ...prev, [campaignId]: false }));
      }
    }
  };

  const toggleAdset = async (adsetId) => {
    const isExpanded = expandedAdsets[adsetId];
    setExpandedAdsets(prev => ({ ...prev, [adsetId]: !isExpanded }));
    if (!isExpanded && !ads[adsetId]) {
      setLoadingAds(prev => ({ ...prev, [adsetId]: true }));
      try {
        const response = await api.get(`/campaigns/adset/${adsetId}/ads?period=${period}`);
        setAds(prev => ({ ...prev, [adsetId]: response.data.ads || [] }));
      } catch (error) {
        console.error('Erro ao carregar ads:', error);
      } finally {
        setLoadingAds(prev => ({ ...prev, [adsetId]: false }));
      }
    }
  };

  const updateStatus = async (type, id, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    setUpdating(prev => ({ ...prev, [`${type}-${id}`]: true }));
    try {
      let endpoint = '';
      if (type === 'campaign') endpoint = `/campaigns/${id}/status`;
      else if (type === 'adset') endpoint = `/campaigns/adset/${id}/status`;
      else if (type === 'ad') endpoint = `/campaigns/ad/${id}/status`;
      const response = await api.post(endpoint, { status: newStatus });
      if (response.data.success) {
        if (type === 'campaign') {
          setData(prev => ({
            ...prev,
            campaigns: (prev?.campaigns || []).map(c => c.campaignId === id ? { ...c, status: newStatus } : c)
          }));
        } else if (type === 'adset') {
          setAdsets(prev => {
            const updated = { ...prev };
            for (const key in updated) {
              updated[key] = updated[key].map(a => a.id === id ? { ...a, status: newStatus } : a);
            }
            return updated;
          });
        } else if (type === 'ad') {
          setAds(prev => {
            const updated = { ...prev };
            for (const key in updated) {
              updated[key] = updated[key].map(a => a.id === id ? { ...a, status: newStatus } : a);
            }
            return updated;
          });
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status');
    } finally {
      setUpdating(prev => ({ ...prev, [`${type}-${id}`]: false }));
    }
  };

  const saveBudget = async () => {
    if (!editBudget || !newBudget) return;
    const { type, id } = editBudget;
    setUpdating(prev => ({ ...prev, [`budget-${id}`]: true }));
    try {
      let endpoint = '';
      if (type === 'campaign') endpoint = `/campaigns/${id}/budget`;
      else if (type === 'adset') endpoint = `/campaigns/adset/${id}/budget`;
      const response = await api.post(endpoint, { dailyBudget: parseFloat(newBudget) });
      if (response.data.success) {
        fetchData();
        setEditBudget(null);
        setNewBudget('');
      }
    } catch (error) {
      console.error('Erro ao atualizar orçamento:', error);
      alert('Erro ao atualizar orçamento');
    } finally {
      setUpdating(prev => ({ ...prev, [`budget-${id}`]: false }));
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key !== key) return { key, direction: 'desc' };
      if (prev.direction === 'desc') return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key: null, direction: null };
      return { key, direction: 'desc' };
    });
  };

  const toggleSelectItem = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === filteredCampaigns.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredCampaigns.map(c => c.campaignId));
    }
  };

  const bulkAction = async (action) => {
    if (selectedItems.length === 0) return;
    const newStatus = action === 'activate' ? 'ACTIVE' : 'PAUSED';
    for (const id of selectedItems) {
      await updateStatus('campaign', id, action === 'activate' ? 'PAUSED' : 'ACTIVE');
    }
    setSelectedItems([]);
  };

  const bulkDuplicate = async () => {
    if (selectedItems.length === 0) return;
    if (!window.confirm('Duplicar ' + selectedItems.length + ' campanha(s)? As cópias serão criadas PAUSADAS, com conjuntos e anúncios.')) return;
    setBulkDupLoading(true);
    const results = [];
    for (const id of selectedItems) {
      try {
        await api.post('/campaigns/campaign/' + id + '/duplicate');
        results.push({ id, ok: true });
      } catch (e) {
        results.push({ id, ok: false, err: e.response?.data?.error || e.message });
      }
    }
    const fails = results.filter(r => !r.ok);
    if (fails.length > 0) window.alert(fails.length + ' duplicação(ões) falharam: ' + fails.map(f => f.err).join('; '));
    else window.alert(results.length + ' campanha(s) duplicada(s) com sucesso! As cópias estão PAUSADAS.');
    setSelectedItems([]);
    fetchData();
    setBulkDupLoading(false);
  };

  const bulkBudgetAction = async () => {
    if (selectedItems.length === 0 || !bulkBudgetValue) return;
    setBulkBudgetLoading(true);
    try {
      for (const id of selectedItems) {
        const campaign = campaigns.find(c => c.campaignId === id);
        if (!campaign) continue;
        let newVal = parseFloat(bulkBudgetValue);
        if (bulkBudgetMode === 'increase') {
          newVal = (campaign.dailyBudget || 0) * (1 + newVal / 100);
        } else if (bulkBudgetMode === 'decrease') {
          newVal = (campaign.dailyBudget || 0) * (1 - newVal / 100);
        }
        if (newVal > 0) {
          try { await api.post('/campaigns/campaign/' + id + '/budget', { dailyBudget: parseFloat(newVal.toFixed(2)) }); } catch (e) { console.error(e); }
        }
      }
      setShowBulkBudget(false);
      setBulkBudgetValue('');
      setSelectedItems([]);
      fetchData();
    } catch (e) { console.error(e); }
    setBulkBudgetLoading(false);
  };

  // ============== COMPUTED ==============
  const summary = data?.summary || {};
  const campaigns = data?.campaigns || data?.data || [];
  const accounts = data?.accounts || data?.data?.accounts || [];

  const businessManagers = useMemo(() => {
    const bms = new Map();
    accounts.forEach(acc => {
      if (acc.businessManagerId && !bms.has(acc.businessManagerId)) {
        bms.set(acc.businessManagerId, acc.businessManagerName || acc.bmName || 'BM ' + acc.businessManagerId);
      }
    });
    return Array.from(bms, ([id, name]) => ({ id, name }));
  }, [accounts]);

  const filteredCampaigns = useMemo(() => {
    let result = campaigns.filter(c => {
      const matchSearch = c.campaignName?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatus = statusFilter === 'all' || c.status === statusFilter;
      const matchAccount = selectedAccounts.length === 0 || selectedAccounts.includes(c.adAccountId);
      const matchBM = selectedBM === 'all' || adAccounts.find(a => a.adAccountId === c.adAccountId)?.businessManagerId === selectedBM;
      return matchSearch && matchStatus && matchAccount && matchBM;
    });
    if (sortConfig.key && sortConfig.direction) {
      result = [...result].sort((a, b) => {
        let aVal = a[sortConfig.key] ?? 0;
        let bVal = b[sortConfig.key] ?? 0;
        if (sortConfig.key === 'name') { aVal = a.campaignName; bVal = b.campaignName; }
        if (sortConfig.key === 'cpa') { aVal = a.spend / (a.purchases || 1); bVal = b.spend / (b.purchases || 1); }
        if (sortConfig.key === 'cpl') { aVal = a.spend / (a.messagesStarted || 1); bVal = b.spend / (b.messagesStarted || 1); }
        if (typeof aVal === 'string') return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      });
    }
    return result;
  }, [campaigns, searchTerm, statusFilter, selectedAccounts, selectedBM, accounts, sortConfig]);

  // Métricas calculadas
  const metrics = useMemo(() => {
    const activeCampaigns = filteredCampaigns.filter(c => c.status === 'ACTIVE');
    const cboCampaigns = activeCampaigns.filter(c => c.dailyBudget);
    const aboCampaigns = activeCampaigns.filter(c => !c.dailyBudget);
    const cboBudget = cboCampaigns.reduce((sum, c) => sum + c.dailyBudget, 0);
    const aboBudget = aboCampaigns.reduce((sum, c) => {
      const campAdsets = adsets[c.campaignId] || [];
      return sum + campAdsets.reduce((s, a) => s + (a.dailyBudget || 0), 0);
    }, 0);
    const totalBudget = cboBudget + aboBudget;
    const totalSpend = filteredCampaigns.reduce((sum, c) => sum + (c.spend || 0), 0);
    const totalPurchases = filteredCampaigns.reduce((sum, c) => sum + (c.purchases || 0), 0);
    const totalMessages = filteredCampaigns.reduce((sum, c) => sum + (c.messagesStarted || 0), 0);
    const totalRevenue = filteredCampaigns.reduce((sum, c) => sum + (c.purchaseValue || 0), 0);
    const cpa = totalPurchases > 0 ? totalSpend / totalPurchases : 0;
    const cpl = totalMessages > 0 ? totalSpend / totalMessages : 0;
    const roi = totalSpend > 0 ? ((totalRevenue - totalSpend) / totalSpend) * 100 : 0;
    const profit = totalRevenue - totalSpend;
    return { totalBudget, totalSpend, totalPurchases, totalMessages, totalRevenue, cpa, cpl, roi, profit };
  }, [filteredCampaigns]);

  // Adsets e Ads filtrados pelas campanhas visíveis
  const filteredAdsets = useMemo(() => {
    // CASCATA (revisao 15/07): campanhas selecionadas (checkbox) mandam; senao o dropdown; senao todas
    const campaignIds = selectedItems.length > 0
      ? selectedItems
      : selectedCampaignFilter === 'all'
        ? filteredCampaigns.map(c => c.campaignId)
        : [selectedCampaignFilter];
    return Object.entries(adsets)
      .filter(([cId]) => campaignIds.includes(cId))
      .flatMap(([cId, sets]) => sets.map(s => ({
        ...s, campaignId: cId,
        campaignName: campaigns.find(c => c.campaignId === cId)?.campaignName || '',
        adAccountName: campaigns.find(c => c.campaignId === cId)?.adAccountName || ''
      })));
  }, [adsets, filteredCampaigns, campaigns, selectedItems, selectedCampaignFilter]);

  const filteredAds = useMemo(() => {
    // CASCATA (revisao 15/07): conjuntos selecionados restringem; senao herda a cascata de campanhas
    const universe = filteredAdsets.map(a => a.id);
    const adsetIds = selectedAdsetItems.length > 0
      ? universe.filter(id => selectedAdsetItems.includes(id))
      : universe;
    return Object.entries(ads)
      .filter(([aId]) => adsetIds.includes(aId))
      .flatMap(([aId, adList]) => adList.map(a => ({
        ...a, adsetId: aId,
        adsetName: filteredAdsets.find(s => s.id === aId)?.name || ''
      })));
  }, [ads, filteredAdsets, selectedAdsetItems]);

  // ============== HELPERS ==============
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString('pt-BR') || '0';
  };

  const getHighlightColor = (metric, value) => {
    const rule = highlightRules.find(r => r.metric === metric && r.enabled);
    if (!rule || value === null || value === undefined) return null;
    const checkCondition = (op, threshold) => {
      if (op === '<=') return value <= threshold;
      if (op === '>=') return value >= threshold;
      if (op === '<') return value < threshold;
      if (op === '>') return value > threshold;
      return false;
    };
    if (checkCondition(rule.green.op, rule.green.value)) return 'bg-green-500/20 text-green-400';
    if (checkCondition(rule.yellow.op, rule.yellow.value)) return 'bg-yellow-500/20 text-yellow-400';
    if (checkCondition(rule.red.op, rule.red.value)) return 'bg-red-500/20 text-red-400';
    return null;
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <ArrowUpDown size={14} className="opacity-30" />;
    if (sortConfig.direction === 'asc') return <ArrowUp size={14} className="text-blue-400" />;
    return <ArrowDown size={14} className="text-blue-400" />;
  };

  const StatusBadge = ({ status, size = 'normal' }) => {
    const isActive = status === 'ACTIVE';
    const sizeClasses = size === 'small' ? 'text-xs px-2 py-0.5' : 'text-sm px-3 py-1';
    return (
      <span className={`flex items-center gap-1 rounded-full font-medium ${sizeClasses} ${isActive ? 'text-green-400 bg-green-400/20' : 'text-gray-400 bg-gray-400/20'}`}>
        <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`}></span>
        {isActive ? 'Ativo' : 'Pausado'}
      </span>
    );
  };

  const ToggleButton = ({ type, id, status, loading: btnLoading }) => (
    <button
      onClick={(e) => { e.stopPropagation(); updateStatus(type, id, status); }}
      disabled={btnLoading}
      className={`p-2 rounded-lg transition-all ${btnLoading ? 'opacity-50' : status === 'ACTIVE' ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'}`}
      title={status === 'ACTIVE' ? 'Pausar' : 'Ativar'}
    >
      {btnLoading ? <Loader2 size={16} className="animate-spin" /> : status === 'ACTIVE' ? <Pause size={16} /> : <Play size={16} />}
    </button>
  );

  // ============== RENDER ==============
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="text-blue-500" />
            Gerenciador de Campanhas
          </h1>
          <p className="text-gray-400">Gerencie suas campanhas do Facebook Ads</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Account Selector */}
          {/* Filtro de BM */}
          {businessManagers.length > 0 && (
            <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
              <Building2 size={16} className="text-gray-400" />
              <select value={selectedBM} onChange={(e) => setSelectedBM(e.target.value)} className="bg-transparent text-white text-sm outline-none cursor-pointer">
                <option value="all" className="bg-gray-800">Todas as BMs</option>
                {businessManagers.map((bm) => (<option key={bm.id} value={bm.id} className="bg-gray-800">{bm.name}</option>))}
              </select>
            </div>
          )}

          <div className="relative">
            <button
              onClick={() => setShowAccountSelector(!showAccountSelector)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white hover:bg-gray-700"
            >
              <CreditCard size={18} className="text-blue-400" />
              <span>{selectedAccounts.length} conta{selectedAccounts.length !== 1 ? 's' : ''}</span>
              <ChevronDown size={16} />
            </button>
            {showAccountSelector && (
              <div className="absolute right-0 mt-2 w-72 bg-gray-800 border border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
                <div className="p-3 border-b border-gray-700 flex justify-between items-center">
                  <span className="text-white font-medium">Contas de Anúncio</span>
                  <button onClick={() => setSelectedAccounts(adAccounts.map(a => a.adAccountId))} className="text-xs text-blue-400 hover:text-blue-300">Todas</button>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {adAccounts.map(acc => (
                    <label key={acc.id} className="flex items-center gap-3 p-3 hover:bg-gray-700/50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedAccounts.includes(acc.adAccountId)}
                        onChange={() => {
                          setSelectedAccounts(prev => 
                            prev.includes(acc.adAccountId) 
                              ? prev.filter(id => id !== acc.adAccountId)
                              : [...prev, acc.adAccountId]
                          );
                        }}
                        className="w-4 h-4 rounded border-gray-600 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{acc.adAccountName}</p>
                        <p className="text-gray-500 text-xs">{acc.businessManagerName || 'Sem BM'}</p>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="p-2 border-t border-gray-700">
                  <button onClick={() => setShowAccountSelector(false)} className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-500">Aplicar</button>
                </div>
              </div>
            )}
          </div>

          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white">
            {periods.map((p) => (<option key={p.value} value={p.value}>{p.label}</option>))}
          </select>
          
          <button onClick={() => setShowHighlightRules(true)} className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700" title="Regras de Destaque">
            <Palette size={20} />
          </button>
          
          <button onClick={() => setShowColumnSettings(true)} className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700" title="Configurar Colunas">
            <Settings size={20} />
          </button>
          
          <button onClick={() => fetchData(true)} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Métricas Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3">
        <div className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 rounded-xl p-4 border border-blue-700/50">
          <div className="flex items-center gap-2 mb-1"><DollarSign className="text-blue-400" size={18} /><span className="text-gray-400 text-xs">Orçamento</span></div>
          <span className="text-xl font-bold text-white">{formatCurrency(metrics.totalBudget)}</span>
          {filteredCampaigns.some(c => !c.dailyBudget) && metrics.totalBudget === 0 && (
            <span className="text-xs text-yellow-400 block mt-1">Expandir campanhas ABO</span>
          )}
        </div>
        <div className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 rounded-xl p-4 border border-purple-700/50">
          <div className="flex items-center gap-2 mb-1"><DollarSign className="text-purple-400" size={18} /><span className="text-gray-400 text-xs">Investido</span></div>
          <span className="text-xl font-bold text-white">{formatCurrency(metrics.totalSpend)}</span>
        </div>
        <div className="bg-gradient-to-br from-green-900/50 to-green-800/30 rounded-xl p-4 border border-green-700/50">
          <div className="flex items-center gap-2 mb-1"><ShoppingCart className="text-green-400" size={18} /><span className="text-gray-400 text-xs">Vendas</span></div>
          <span className="text-xl font-bold text-green-400">{formatNumber(metrics.totalPurchases)}</span>
        </div>
        <div className="bg-gradient-to-br from-orange-900/50 to-orange-800/30 rounded-xl p-4 border border-orange-700/50">
          <div className="flex items-center gap-2 mb-1"><Target className="text-orange-400" size={18} /><span className="text-gray-400 text-xs">CPA</span></div>
          <span className={`text-xl font-bold ${getHighlightColor('cpa', metrics.cpa) || 'text-white'}`}>{formatCurrency(metrics.cpa)}</span>
        </div>
        <div className="bg-gradient-to-br from-emerald-900/50 to-emerald-800/30 rounded-xl p-4 border border-emerald-700/50">
          <div className="flex items-center gap-2 mb-1"><DollarSign className="text-emerald-400" size={18} /><span className="text-gray-400 text-xs">Comissões</span></div>
          <span className="text-xl font-bold text-emerald-400">{formatCurrency(metrics.totalRevenue)}</span>
        </div>
        <div className="bg-gradient-to-br from-cyan-900/50 to-cyan-800/30 rounded-xl p-4 border border-cyan-700/50">
          <div className="flex items-center gap-2 mb-1"><TrendingUp className="text-cyan-400" size={18} /><span className="text-gray-400 text-xs">ROI</span></div>
          <span className={`text-xl font-bold ${metrics.roi > 0 ? 'text-cyan-400' : 'text-red-400'}`}>{metrics.roi.toFixed(0)}%</span>
        </div>
        <div className="bg-gradient-to-br from-teal-900/50 to-teal-800/30 rounded-xl p-4 border border-teal-700/50">
          <div className="flex items-center gap-2 mb-1"><DollarSign className="text-teal-400" size={18} /><span className="text-gray-400 text-xs">Lucro</span></div>
          <span className={`text-xl font-bold ${metrics.profit > 0 ? 'text-teal-400' : 'text-red-400'}`}>{formatCurrency(metrics.profit)}</span>
        </div>
        <div className="bg-gradient-to-br from-pink-900/50 to-pink-800/30 rounded-xl p-4 border border-pink-700/50">
          <div className="flex items-center gap-2 mb-1"><MessageCircle className="text-pink-400" size={18} /><span className="text-gray-400 text-xs">Mensagens</span></div>
          <span className="text-xl font-bold text-pink-400">{formatNumber(metrics.totalMessages)}</span>
        </div>
        <div className="bg-gradient-to-br from-amber-900/50 to-amber-800/30 rounded-xl p-4 border border-amber-700/50">
          <div className="flex items-center gap-2 mb-1"><MessageCircle className="text-amber-400" size={18} /><span className="text-gray-400 text-xs">CPL</span></div>
          <span className="text-xl font-bold text-amber-400">{formatCurrency(metrics.cpl)}</span>
        </div>
      </div>

      {/* Guias */}
      <div className="flex items-center gap-1 bg-gray-800 p-1 rounded-xl w-fit">
        {[
          { id: 'campaigns', label: 'Campanhas', icon: Layers, count: filteredCampaigns.length },
          { id: 'adsets', label: 'Conjuntos', icon: Target, count: filteredAdsets.length },
          { id: 'ads', label: 'Anúncios', icon: Eye, count: Object.values(ads).flat().length },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}
          >
            <tab.icon size={18} />
            <span>{tab.label}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-blue-500' : 'bg-gray-700'}`}>{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Bulk Actions */}
      {selectedItems.length > 0 && (
        <div className="flex items-center gap-4 p-4 bg-blue-900/30 border border-blue-700 rounded-xl">
          <span className="text-blue-300">{selectedItems.length} campanha{selectedItems.length > 1 ? 's' : ''} selecionada{selectedItems.length > 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button onClick={() => bulkAction('activate')} className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-500"><Play size={14} />Ativar</button>
            <button onClick={() => bulkAction('pause')} className="flex items-center gap-1 px-3 py-1.5 bg-yellow-600 text-white rounded-lg text-sm hover:bg-yellow-500"><Pause size={14} />Pausar</button>
            <button onClick={() => setShowBulkBudget(true)} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-500"><DollarSign size={14} />Editar Orçamento</button>
            <button onClick={bulkDuplicate} disabled={bulkDupLoading} className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-lg text-sm"><Copy size={14} />{bulkDupLoading ? 'Duplicando...' : 'Duplicar'}</button>
          </div>
          <button onClick={() => setSelectedItems([])} className="ml-auto text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3 bg-gray-800 rounded-xl p-4 border border-gray-700">
        <Filter size={18} className="text-gray-400" />
        <div className="relative flex-1 min-w-[200px]">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input type="text" placeholder={activeTab === "adsets" ? "Buscar conjunto..." : activeTab === "ads" ? "Buscar anúncio..." : "Buscar campanha..."} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-500" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white">
          <option value="all">Todos Status</option>
          <option value="ACTIVE">Ativos</option>
          <option value="PAUSED">Pausados</option>
        </select>
        <span className="text-gray-500 text-sm">{activeTab === "campaigns" ? filteredCampaigns.length + " campanhas" : activeTab === "adsets" ? filteredAdsets.length + " conjuntos" : filteredAds.length + " anúncios"}</span>
      </div>

      {/* Tabela de Campanhas */}
      {activeTab === 'campaigns' && <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-blue-500" size={40} /></div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="text-center py-20 text-gray-500">Nenhuma campanha encontrada</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-gray-900/50 border-b border-gray-700">
                <tr>
                  <th className="p-3 w-10">
                    <input type="checkbox" checked={selectedItems.length === filteredCampaigns.length && filteredCampaigns.length > 0} onChange={toggleSelectAll} className="w-4 h-4 rounded border-gray-600 text-blue-600" />
                  </th>
                  {columns.filter(c => c.visible).map(col => (
                    <th key={col.id} className="p-3 text-left text-gray-400 text-sm font-medium cursor-pointer hover:text-white relative group/th" style={{ width: columnWidths[col.id] || 'auto', minWidth: 60 }} onClick={() => !col.fixed && handleSort(col.id)}>
                      <div className="flex items-center gap-2">
                        {col.label}
                        {!col.fixed && <SortIcon column={col.id} />}
                      </div>
                      <div onMouseDown={(e) => handleResizeStart(e, col.id)} className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize bg-transparent hover:bg-blue-500/50 group-hover/th:bg-gray-600/30" />
                    </th>
                  ))}
                  <th className="p-3 w-24 text-gray-400 text-sm font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredCampaigns.map((campaign) => {
                  const cpa = campaign.purchases > 0 ? campaign.spend / campaign.purchases : 0;
                  const cpl = campaign.messagesStarted > 0 ? campaign.spend / campaign.messagesStarted : 0;
                  const roas = campaign.spend > 0 ? (campaign.purchaseValue || 0) / campaign.spend : 0;
                  return (
                    <React.Fragment key={campaign.campaignId}>
                      <tr className={`hover:bg-gray-700/50 transition-colors ${selectedItems.includes(campaign.campaignId) ? 'bg-blue-900/20' : ''}`}>
                        <td className="p-3">
                          <input type="checkbox" checked={selectedItems.includes(campaign.campaignId)} onChange={() => toggleSelectItem(campaign.campaignId)} className="w-4 h-4 rounded border-gray-600 text-blue-600" />
                        </td>
                        {columns.filter(c => c.visible).map(col => (
                          <td key={col.id} className="p-3" style={{ width: columnWidths[col.id] || 'auto', minWidth: 60 }}>
                            {col.id === 'name' && (
                              <div className="flex items-center gap-2 cursor-pointer" onClick={() => toggleCampaign(campaign.campaignId)}>
                                {expandedCampaigns[campaign.campaignId] ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
                                <Layers size={16} className="text-blue-400" />
                                <span className="text-white font-medium overflow-hidden text-ellipsis whitespace-nowrap block max-w-[200px]" title={campaign.campaignName}>{campaign.campaignName}</span>
                              </div>
                            )}
                            {col.id === 'account' && <span className="text-gray-400 text-sm">{campaign.adAccountName}</span>}
                            {col.id === 'status' && <StatusBadge status={campaign.status} size="small" />}
                            {col.id === 'budget' && <span className="text-white">{campaign.dailyBudget ? formatCurrency(campaign.dailyBudget) : <span className="text-gray-500 text-xs">ABO</span>}</span>}
                            {col.id === 'spend' && <span className="text-blue-400">{formatCurrency(campaign.spend)}</span>}
                            {col.id === 'purchases' && <span className="text-green-400 font-medium">{campaign.purchases || 0}</span>}
                            {col.id === 'cpa' && <span className={`px-2 py-1 rounded ${getHighlightColor('cpa', cpa) || ''}`}>{formatCurrency(cpa)}</span>}
                            {col.id === 'roas' && <span className={`px-2 py-1 rounded ${getHighlightColor('roas', roas) || ''}`}>{roas.toFixed(2)}x</span>}
                            {col.id === 'messages' && <span className="text-pink-400">{campaign.messagesStarted || 0}</span>}
                            {col.id === 'cpl' && <span className="text-amber-400">{formatCurrency(cpl)}</span>}
                            {col.id === 'clicks' && <span className="text-cyan-400">{formatNumber(campaign.clicks)}</span>}
                            {col.id === 'ctr' && <span className="text-green-400">{campaign.ctr?.toFixed(2)}%</span>}
                            {col.id === 'cpm' && <span className="text-purple-400">{formatCurrency(campaign.cpm)}</span>}
                            {col.id === 'impressions' && <span className="text-gray-300">{formatNumber(campaign.impressions)}</span>}
                            {col.id === 'reach' && <span className="text-gray-300">{formatNumber(campaign.reach)}</span>}
                            {col.id === 'purchaseValue' && <span className="text-green-400">{formatCurrency(campaign.purchaseValue || 0)}</span>}
                            {col.id === 'inlineLinkClicks' && <span className="text-cyan-400">{formatNumber(campaign.inlineLinkClicks || 0)}</span>}
                            {col.id === 'ctrLink' && <span className="text-green-400">{(campaign.inlineLinkClickCtr || 0).toFixed(2)}%</span>}
                            {col.id === 'frequency' && <span className="text-gray-300">{(campaign.frequency || 0).toFixed(2)}</span>}
                            {col.id === 'landingPageViews' && <span className="text-cyan-400">{formatNumber(campaign.landingPageViews || 0)}</span>}
                            {col.id === 'costPerLandingPageView' && <span className="text-amber-400">{formatCurrency(campaign.costPerLandingPageView || 0)}</span>}
                            {col.id === 'postEngagement' && <span className="text-pink-400">{formatNumber(campaign.postEngagement || 0)}</span>}
                            {col.id === 'postReactions' && <span className="text-pink-400">{formatNumber(campaign.postReactions || 0)}</span>}
                            {col.id === 'postComments' && <span className="text-pink-400">{formatNumber(campaign.postComments || 0)}</span>}
                            {col.id === 'postShares' && <span className="text-pink-400">{formatNumber(campaign.postShares || 0)}</span>}
                            {col.id === 'thruplays' && <span className="text-violet-400">{formatNumber(campaign.thruplays || 0)}</span>}
                            {col.id === 'costPerThruplay' && <span className="text-amber-400">{formatCurrency(campaign.thruplays > 0 ? campaign.spend / campaign.thruplays : 0)}</span>}
                            {col.id === 'videoPlays' && <span className="text-violet-400">{formatNumber(campaign.videoPlays || 0)}</span>}
                            {col.id === 'video3s' && <span className="text-violet-400">{formatNumber(campaign.video3s || 0)}</span>}
                            {col.id === 'videoP25' && <span className="text-violet-400">{formatNumber(campaign.videoP25 || 0)}</span>}
                            {col.id === 'videoP50' && <span className="text-violet-400">{formatNumber(campaign.videoP50 || 0)}</span>}
                            {col.id === 'videoP75' && <span className="text-violet-400">{formatNumber(campaign.videoP75 || 0)}</span>}
                            {col.id === 'videoP95' && <span className="text-violet-400">{formatNumber(campaign.videoP95 || 0)}</span>}
                            {col.id === 'videoP100' && <span className="text-violet-400">{formatNumber(campaign.videoP100 || 0)}</span>}
                            {col.id === 'videoAvgTime' && <span className="text-gray-300">{(campaign.videoAvgTime || 0).toFixed(0)}s</span>}
                          </td>
                        ))}
                        <td className="p-3">
                          <div className="flex items-center gap-1 relative">
                            <ToggleButton type="campaign" id={campaign.campaignId} status={campaign.status} loading={updating[`campaign-${campaign.campaignId}`]} />
                            <button onClick={(e) => { e.stopPropagation(); setEditBudget({ type: 'campaign', id: campaign.campaignId, current: campaign.dailyBudget }); setNewBudget(campaign.dailyBudget ? campaign.dailyBudget.toString() : ''); }} className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg" title="Editar orçamento"><Edit3 size={16} /></button>
                            <button onClick={(e) => { e.stopPropagation(); setActionMenu(actionMenu === campaign.campaignId ? null : campaign.campaignId); }} className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"><MoreVertical size={16} /></button>
                            {actionMenu === campaign.campaignId && (
                              <div className="absolute right-0 top-10 w-56 bg-gray-800 border border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
                                <button onClick={() => { window.open(`https://www.facebook.com/adsmanager/manage/campaigns?act=${campaign.adAccountId?.replace('act_','')}&selected_campaign_ids=${campaign.campaignId}`, '_blank'); setActionMenu(null); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-700/50 text-white text-sm"><ExternalLink size={16} className="text-blue-400" />Abrir no Ads Manager</button>
                                <button onClick={() => { window.open(`https://business.facebook.com/latest/ad_center?act=${campaign.adAccountId?.replace('act_','')}`, '_blank'); setActionMenu(null); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-700/50 text-white text-sm"><Building2 size={16} className="text-purple-400" />Link da BM</button>
                                <button onClick={() => { navigator.clipboard.writeText(campaign.campaignId); setActionMenu(null); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-700/50 text-white text-sm"><Copy size={16} className="text-gray-400" />Copiar ID da Campanha</button>
                                <button onClick={() => { navigator.clipboard.writeText(campaign.campaignName); setActionMenu(null); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-700/50 text-white text-sm"><Copy size={16} className="text-gray-400" />Copiar Nome</button>
                                <div className="border-t border-gray-700" />
                                <button onClick={() => { window.open(`https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=BR&q=${encodeURIComponent(campaign.campaignName)}`, '_blank'); setActionMenu(null); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-700/50 text-white text-sm"><Search size={16} className="text-green-400" />Buscar na Ad Library</button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* AdSets expandidos */}
                      {expandedCampaigns[campaign.campaignId] && (
                        <tr><td colSpan={columns.filter(c => c.visible).length + 2} className="p-0 bg-gray-900/50">
                          {loadingAdsets[campaign.campaignId] ? (
                            <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin text-blue-500" size={24} /></div>
                          ) : (adsets[campaign.campaignId] || []).length === 0 ? (
                            <div className="text-center py-8 text-gray-500 text-sm">Nenhum conjunto encontrado</div>
                          ) : (
                            <div className="divide-y divide-gray-700/50">
                              {(adsets[campaign.campaignId] || []).map((adset) => (
                                <React.Fragment key={adset.id}>
                                <div className="flex items-center gap-4 p-4 pl-12 hover:bg-gray-700/30">
                                  <button onClick={() => toggleAdset(adset.id)} className="text-gray-400 hover:text-white">{expandedAdsets[adset.id] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</button>
                                  <Target size={14} className="text-purple-400" />
                                  <span className="text-gray-200 flex-1">{adset.name}</span>
                                  <span className="text-blue-400">R$ {adset.spend?.toFixed(2)}</span>
                                  <span className="text-green-400">{adset.messagesStarted || 0} msgs</span>
                                  <StatusBadge status={adset.status} size="small" />
                                  <ToggleButton type="adset" id={adset.id} status={adset.status} loading={updating[`adset-${adset.id}`]} />
                                </div>
                                {expandedAdsets[adset.id] && (
                                  <div className="pl-20 border-t border-gray-700/50">
                                    {loadingAds[adset.id] ? (
                                      <div className="p-3 text-gray-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Carregando anúncios...</div>
                                    ) : (ads[adset.id] || []).length === 0 ? (
                                      <div className="p-3 text-gray-500 text-sm">Nenhum anúncio encontrado</div>
                                    ) : (
                                      (ads[adset.id] || []).map(ad => (
                                        <div key={ad.id} className="flex items-center gap-4 p-3 hover:bg-gray-700/20 border-b border-gray-700/30">
                                          <Image size={14} className="text-yellow-400 flex-shrink-0" />
                                          <span className="text-gray-300 flex-1 text-sm">{ad.name}</span>
                                          <span className="text-blue-400 text-sm">R$ {(ad.spend || 0).toFixed(2)}</span>
                                          <span className="text-green-400 text-sm">{ad.messagesStarted || 0} msgs</span>
                                          <StatusBadge status={ad.status} size="small" />
                                          <ToggleButton type="ad" id={ad.id} status={ad.status} loading={updating[`ad-${ad.id}`]} />
                                        </div>
                                      ))
                                    )}
                                  </div>
                                )}
                                </React.Fragment>
                              ))}
                            </div>
                          )}
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>}

      {/* Tabela de Conjuntos */}
      {activeTab === 'adsets' && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="p-3 border-b border-gray-700 flex items-center gap-3">
            <Filter size={16} className="text-gray-400" />
            <select value={selectedCampaignFilter} onChange={e => setSelectedCampaignFilter(e.target.value)} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm" disabled={selectedItems.length > 0}>
              <option value="all">Todas as campanhas</option>
              {filteredCampaigns.map(c => <option key={c.campaignId} value={c.campaignId}>{c.campaignName?.substring(0,50)}</option>)}
            </select>
            {selectedItems.length > 0 && (
              <span className="flex items-center gap-2 text-xs text-blue-400 bg-blue-500/10 px-3 py-1.5 rounded-lg">
                Mostrando conjuntos de {selectedItems.length} campanha{selectedItems.length > 1 ? 's' : ''} selecionada{selectedItems.length > 1 ? 's' : ''}
                <button onClick={() => setSelectedItems([])} className="text-blue-300 hover:text-white underline">limpar</button>
              </span>
            )}
          </div>
          {filteredAdsets.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              <Target size={40} className="mx-auto mb-3 opacity-50" />
              <p>Nenhum conjunto carregado</p>
              <p className="text-sm mt-1">Expanda campanhas na aba Campanhas para carregar conjuntos</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900/50 border-b border-gray-700">
                  <tr>
                    <th className="p-3 w-10"></th>
                    <th className="p-3 text-left text-gray-400 text-sm">Conjunto</th>
                    <th className="p-3 text-left text-gray-400 text-sm">Campanha</th>
                    <th className="p-3 text-center text-gray-400 text-sm">Status</th>
                    <th className="p-3 text-right text-gray-400 text-sm">Gasto</th>
                    <th className="p-3 text-right text-gray-400 text-sm">Mensagens</th>
                    <th className="p-3 text-right text-gray-400 text-sm">Cliques</th>
                    <th className="p-3 text-right text-gray-400 text-sm">CPL</th>
                    <th className="p-3 w-20 text-gray-400 text-sm">Acoes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredAdsets.map((adset) => (
                    <tr key={adset.id} className="hover:bg-gray-700/50">
                      <td className="p-3"><input type="checkbox" checked={selectedAdsetItems.includes(adset.id)} onChange={() => setSelectedAdsetItems(prev => prev.includes(adset.id) ? prev.filter(x => x !== adset.id) : [...prev, adset.id])} className="w-4 h-4 rounded border-gray-600 text-blue-600" /></td>
                      <td className="p-3"><div className="flex items-center gap-2"><Target size={14} className="text-purple-400" /><span className="text-white font-medium">{adset.name}</span></div></td>
                      <td className="p-3 text-gray-400 text-sm truncate max-w-[150px]" title={adset.campaignName}>{adset.campaignName}</td>
                      <td className="p-3 text-center"><StatusBadge status={adset.status} size="small" /></td>
                      <td className="p-3 text-right text-blue-400">{formatCurrency(adset.spend)}</td>
                      <td className="p-3 text-right text-pink-400">{adset.messagesStarted || 0}</td>
                      <td className="p-3 text-right text-cyan-400">{formatNumber(adset.clicks || 0)}</td>
                      <td className="p-3 text-right text-amber-400">{adset.messagesStarted > 0 ? formatCurrency(adset.spend / adset.messagesStarted) : '-'}</td>
                      <td className="p-3"><ToggleButton type="adset" id={adset.id} status={adset.status} loading={updating['adset-' + adset.id]} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tabela de Anúncios */}
      {activeTab === 'ads' && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          {(selectedAdsetItems.length > 0 || selectedItems.length > 0) && (
            <div className="p-3 border-b border-gray-700">
              <span className="flex items-center gap-2 text-xs text-blue-400 bg-blue-500/10 px-3 py-1.5 rounded-lg w-fit">
                {selectedAdsetItems.length > 0
                  ? `Mostrando anúncios de ${selectedAdsetItems.length} conjunto${selectedAdsetItems.length > 1 ? 's' : ''} selecionado${selectedAdsetItems.length > 1 ? 's' : ''}`
                  : `Mostrando anúncios de ${selectedItems.length} campanha${selectedItems.length > 1 ? 's' : ''} selecionada${selectedItems.length > 1 ? 's' : ''}`}
                <button onClick={() => { setSelectedAdsetItems([]); setSelectedItems([]); }} className="text-blue-300 hover:text-white underline">limpar</button>
              </span>
            </div>
          )}
          {filteredAds.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              <Eye size={40} className="mx-auto mb-3 opacity-50" />
              <p>Nenhum anúncio carregado</p>
              <p className="text-sm mt-1">Expanda conjuntos na aba Campanhas para carregar anúncios</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900/50 border-b border-gray-700">
                  <tr>
                    <th className="p-3 text-left text-gray-400 text-sm">Anúncio</th>
                    <th className="p-3 text-left text-gray-400 text-sm">Conjunto</th>
                    <th className="p-3 text-center text-gray-400 text-sm">Status</th>
                    <th className="p-3 text-right text-gray-400 text-sm">Gasto</th>
                    <th className="p-3 text-right text-gray-400 text-sm">Impressoes</th>
                    <th className="p-3 text-right text-gray-400 text-sm">Cliques</th>
                    <th className="p-3 text-right text-gray-400 text-sm">CTR</th>
                    <th className="p-3 w-20 text-gray-400 text-sm">Acoes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredAds.map((ad) => (
                    <tr key={ad.id} className="hover:bg-gray-700/50">
                      <td className="p-3"><div className="flex items-center gap-2"><Eye size={14} className="text-cyan-400" /><span className="text-white font-medium">{ad.name}</span></div></td>
                      <td className="p-3 text-gray-400 text-sm">{ad.adsetName}</td>
                      <td className="p-3 text-center"><StatusBadge status={ad.status} size="small" /></td>
                      <td className="p-3 text-right text-blue-400">{formatCurrency(ad.spend)}</td>
                      <td className="p-3 text-right text-gray-300">{formatNumber(ad.impressions || 0)}</td>
                      <td className="p-3 text-right text-cyan-400">{formatNumber(ad.clicks || 0)}</td>
                      <td className="p-3 text-right text-green-400">{(ad.ctr || 0).toFixed(2)}%</td>
                      <td className="p-3"><ToggleButton type="ad" id={ad.id} status={ad.status} loading={updating['ad-' + ad.id]} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal Editar Orçamento */}
      {showBulkBudget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Editar Orçamento em Massa</h3>
              <button onClick={() => setShowBulkBudget(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <p className="text-gray-400 text-sm mb-4">{selectedItems.length} campanha{selectedItems.length > 1 ? 's' : ''} selecionada{selectedItems.length > 1 ? 's' : ''}</p>
            <div className="flex gap-2 mb-4">
              {[{ v: 'set', l: 'Valor fixo' }, { v: 'increase', l: 'Aumentar %' }, { v: 'decrease', l: 'Diminuir %' }].map(m => (
                <button key={m.v} onClick={() => setBulkBudgetMode(m.v)} className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${bulkBudgetMode === m.v ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'}`}>{m.l}</button>
              ))}
            </div>
            <div className="mb-4">
              <label className="text-gray-400 text-sm mb-2 block">{bulkBudgetMode === 'set' ? 'Novo or\u00E7amento (R$)' : 'Percentual (%)'}</label>
              <input type="number" value={bulkBudgetValue} onChange={(e) => setBulkBudgetValue(e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-lg" placeholder={bulkBudgetMode === 'set' ? '0.00' : '20'} step={bulkBudgetMode === 'set' ? '0.01' : '1'} />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowBulkBudget(false)} className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg">Cancelar</button>
              <button onClick={bulkBudgetAction} disabled={bulkBudgetLoading || !bulkBudgetValue} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50 flex items-center justify-center gap-2">
                {bulkBudgetLoading ? <Loader2 size={18} className="animate-spin" /> : 'Aplicar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editBudget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Editar Orçamento Diário</h3>
              <button onClick={() => setEditBudget(null)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="mb-4">
              <label className="text-gray-400 text-sm mb-2 block">Novo orçamento (R$)</label>
              <input type="number" value={newBudget} onChange={(e) => setNewBudget(e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white text-lg" placeholder="0.00" step="0.01" />
              <p className="text-gray-500 text-xs mt-2">Atual: R$ {editBudget.current?.toFixed(2)}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setEditBudget(null)} className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600">Cancelar</button>
              <button onClick={saveBudget} disabled={updating[`budget-${editBudget.id}`] || !newBudget} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50 flex items-center justify-center gap-2">
                {updating[`budget-${editBudget.id}`] ? <Loader2 size={18} className="animate-spin" /> : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Configurar Colunas */}
      {showColumnSettings && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><Settings size={20} />Personalizar Colunas</h3>
              <button onClick={() => setShowColumnSettings(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            {/* Predefinições */}
            <div className="mb-4">
              <p className="text-gray-400 text-xs mb-2">Predefinições</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Padrão', cols: ['name','status','budget','spend','purchases','cpa','roas'] },
                  { label: 'Desempenho', cols: ['name','status','spend','impressions','reach','frequency','cpm','purchases','cpa','roas'] },
                  { label: 'Desempenho e Cliques', cols: ['name','status','spend','impressions','clicks','ctr','cpc','cpm','inlineLinkClicks','ctrLink','landingPageViews','costPerLandingPageView'] },
                  { label: 'Engajamento', cols: ['name','status','spend','postEngagement','postReactions','postComments','postShares','frequency'] },
                  { label: 'Engajamento com o Vídeo', cols: ['name','status','spend','videoPlays','video3s','thruplays','costPerThruplay','videoP25','videoP50','videoP75','videoP95','videoP100','videoAvgTime'] },
                  { label: 'Engajamento com as mensagens', cols: ['name','status','spend','messages','cpl','purchases','cpa','roas'] },
                  ...JSON.parse(localStorage.getItem('jtracker_custom_presets') || '[]'),
                ].map(preset => (
                  <button key={preset.label} onClick={() => setColumns(prev => prev.map(c => ({ ...c, visible: c.fixed || preset.cols.includes(c.id) })))}
                    className="px-3 py-1.5 bg-gray-700 hover:bg-blue-600 text-white text-xs rounded-lg transition-colors">
                    {preset.label}
                  </button>
                ))}
                <button onClick={() => setColumns(prev => prev.map(c => ({ ...c, visible: true })))}
                  className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-400 text-xs rounded-lg transition-colors">
                  Todas
                </button>
                <button onClick={() => {
                  const nome = window.prompt('Nome da predefinição personalizada:');
                  if (!nome) return;
                  const atual = columns.filter(c => c.visible).map(c => c.id);
                  const salvos = JSON.parse(localStorage.getItem('jtracker_custom_presets') || '[]');
                  localStorage.setItem('jtracker_custom_presets', JSON.stringify([...salvos.filter(p => p.label !== nome), { label: nome, cols: atual }]));
                  window.alert('Predefinição "' + nome + '" salva!');
                }} className="px-3 py-1.5 bg-blue-600/30 hover:bg-blue-600 text-blue-300 text-xs rounded-lg transition-colors">
                  + Criar predefinição
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {columns.map((col, idx) => (
                <label key={col.id} className={`flex items-center gap-3 p-3 rounded-lg ${col.fixed ? 'bg-gray-700/50 opacity-60' : 'bg-gray-700/30 hover:bg-gray-700/50 cursor-pointer'}`}>
                  <input type="checkbox" checked={col.visible} disabled={col.fixed} onChange={() => setColumns(prev => prev.map((c, i) => i === idx ? { ...c, visible: !c.visible } : c))} className="w-4 h-4 rounded border-gray-600 text-blue-600 disabled:opacity-50" />
                  <span className="text-white">{col.label}</span>
                  {col.fixed && <span className="text-xs text-gray-500">(fixo)</span>}
                </label>
              ))}
            </div>
            <button onClick={() => setShowColumnSettings(false)} className="w-full mt-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500">Fechar</button>
          </div>
        </div>
      )}

      {/* Modal Regras de Destaque */}
      {showHighlightRules && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><Palette size={20} />Regras de Destaque</h3>
              <button onClick={() => setShowHighlightRules(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              {highlightRules.map((rule, idx) => (
                <div key={rule.id} className="p-4 bg-gray-700/50 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <input type="text" value={rule.name} onChange={(e) => setHighlightRules(prev => prev.map((r, i) => i === idx ? { ...r, name: e.target.value } : r))} className="bg-gray-600 border border-gray-500 rounded text-white text-sm px-2 py-1 w-32 focus:border-blue-500 focus:outline-none" />
                      <select value={rule.metric} onChange={(e) => setHighlightRules(prev => prev.map((r, i) => i === idx ? { ...r, metric: e.target.value } : r))} className="bg-gray-600 border-0 rounded text-white text-xs px-2 py-1">
                        <option value="cpa">CPA</option>
                        <option value="roas">ROAS</option>
                        <option value="ctr">CTR</option>
                        <option value="cpm">CPM</option>
                        <option value="cpc">CPC</option>
                        <option value="spend">Gasto</option>
                        <option value="costPerMessage">CPM Msg</option>
                        <option value="leads">Leads</option>
                        <option value="purchases">Compras</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setHighlightRules(prev => prev.filter((_, i) => i !== idx))} className="text-gray-500 hover:text-red-400"><Trash2 size={14} /></button>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={rule.enabled} onChange={() => setHighlightRules(prev => prev.map((r, i) => i === idx ? { ...r, enabled: !r.enabled } : r))} className="w-4 h-4 rounded border-gray-600 text-blue-600" />
                      </label>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-green-500/10 border border-green-700/30 rounded-lg p-2">
                      <p className="text-green-400 text-xs font-medium mb-1">Verde</p>
                      <div className="flex gap-1">
                        <select value={rule.green.op} onChange={(e) => setHighlightRules(prev => prev.map((r, i) => i === idx ? { ...r, green: { ...r.green, op: e.target.value } } : r))} className="bg-gray-700 border-0 rounded text-white text-xs px-1 py-1 w-12">{['<=','>=','<','>'].map(o => <option key={o} value={o}>{o}</option>)}</select>
                        <input type="number" value={rule.green.value} onChange={(e) => setHighlightRules(prev => prev.map((r, i) => i === idx ? { ...r, green: { ...r.green, value: parseFloat(e.target.value) || 0 } } : r))} className="bg-gray-700 border-0 rounded text-white text-xs px-2 py-1 w-16" step="0.1" />
                      </div>
                    </div>
                    <div className="bg-yellow-500/10 border border-yellow-700/30 rounded-lg p-2">
                      <p className="text-yellow-400 text-xs font-medium mb-1">Amarelo</p>
                      <div className="flex gap-1">
                        <select value={rule.yellow.op} onChange={(e) => setHighlightRules(prev => prev.map((r, i) => i === idx ? { ...r, yellow: { ...r.yellow, op: e.target.value } } : r))} className="bg-gray-700 border-0 rounded text-white text-xs px-1 py-1 w-12">{['<=','>=','<','>'].map(o => <option key={o} value={o}>{o}</option>)}</select>
                        <input type="number" value={rule.yellow.value} onChange={(e) => setHighlightRules(prev => prev.map((r, i) => i === idx ? { ...r, yellow: { ...r.yellow, value: parseFloat(e.target.value) || 0 } } : r))} className="bg-gray-700 border-0 rounded text-white text-xs px-2 py-1 w-16" step="0.1" />
                      </div>
                    </div>
                    <div className="bg-red-500/10 border border-red-700/30 rounded-lg p-2">
                      <p className="text-red-400 text-xs font-medium mb-1">Vermelho</p>
                      <div className="flex gap-1">
                        <select value={rule.red.op} onChange={(e) => setHighlightRules(prev => prev.map((r, i) => i === idx ? { ...r, red: { ...r.red, op: e.target.value } } : r))} className="bg-gray-700 border-0 rounded text-white text-xs px-1 py-1 w-12">{['<=','>=','<','>'].map(o => <option key={o} value={o}>{o}</option>)}</select>
                        <input type="number" value={rule.red.value} onChange={(e) => setHighlightRules(prev => prev.map((r, i) => i === idx ? { ...r, red: { ...r.red, value: parseFloat(e.target.value) || 0 } } : r))} className="bg-gray-700 border-0 rounded text-white text-xs px-2 py-1 w-16" step="0.1" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={() => setHighlightRules(prev => [...prev, { id: 'custom_' + Date.now(), name: 'Nova Regra', enabled: true, metric: 'cpa', green: { op: '<=', value: 100 }, yellow: { op: '<=', value: 200 }, red: { op: '>', value: 200 } }])} className="w-full py-2 border-2 border-dashed border-gray-600 text-gray-400 rounded-lg hover:border-blue-500 hover:text-blue-400 text-sm">+ Adicionar Regra</button>
            </div>
            <button onClick={() => setShowHighlightRules(false)} className="w-full mt-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500">Fechar</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
